import { useEffect, useRef } from "react";
import { useDailyChallenges } from "@/hooks/use-daily-challenge";
import { useGamificationToast } from "@/contexts/GamificationToastContext";
import type { ClaimRewardResult } from "@/hooks/use-daily-challenge";

export interface UseAutoClaimRewardsOptions {
    /** Set false to park the auto-claimer without unmounting it. */
    enabled?: boolean;
    /** Grace period before the first claim, so navigation settles first. */
    delayMs?: number;
    /** Hard cap on the ids held in the in-flight set before it is cleared. */
    maxTrackedIds?: number;
}

/**
 * Claim daily-challenge rewards the moment they are earned — no click.
 *
 * The manual "Claim N rewards" button this replaces (avatar menu) meant a
 * learner could finish a challenge and leave the XP sitting in the row until
 * they happened to open the right menu. The claim itself is idempotent
 * server-side: `claim_challenge_reward` filters on `is_claimed = FALSE` and
 * answers `success = false` for a challenge that was already claimed, so a
 * retry after a dropped response cannot double-award.
 *
 * Every claim in one pass is folded into a **single** XP toast. Firing one
 * per challenge would stack four identical cards in the corner on the first
 * load after a long day, and the learner only needs the total plus the new
 * level.
 */
export const useAutoClaimRewards = ({
    enabled = true,
    delayMs = 1200,
    maxTrackedIds = 50,
}: UseAutoClaimRewardsOptions = {}) => {
    const { challenges, claimReward, isLoading } = useDailyChallenges();
    const { showXPGain } = useGamificationToast();

    // Claimed ids for this mount. Guards the window between a successful
    // claim and the query invalidation that flips `is_claimed` in the cache,
    // and absorbs a StrictMode double-invoke of the effect.
    const claimedRef = useRef<Set<string>>(new Set());
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!enabled || isLoading) return;

        // Resolve from the latest cache snapshot, not from the render that
        // scheduled this effect — the query may have been invalidated while
        // the grace-period timer was still pending.
        const pending = challenges.filter(
            (c) => c.is_completed && !c.is_claimed && !claimedRef.current.has(c.id),
        );
        if (pending.length === 0) return;

        let cancelled = false;

        timerRef.current = setTimeout(async () => {
            timerRef.current = null;
            if (cancelled) return;

            let totalXp = 0;
            let lastResult: ClaimRewardResult | null = null;
            let claimedAny = false;

            for (const challenge of pending) {
                claimedRef.current.add(challenge.id);
                try {
                    const result = await claimReward(challenge.id);
                    if (result?.success) {
                        claimedAny = true;
                        totalXp += result.xp_awarded || 0;
                        lastResult = result;
                    }
                } catch (error) {
                    // Unknown outcome — leave the id tracked anyway so a
                    // failing claim cannot spin; the next mount/refetch retries.
                    console.error(`Failed to auto-claim challenge ${challenge.id}:`, error);
                }
            }

            if (cancelled || !claimedAny || !lastResult) return;

            showXPGain({
                xpAmount: totalXp,
                newXp: lastResult.new_xp,
                newLevel: lastResult.new_level,
                levelUp: lastResult.level_up,
                currentStreak: lastResult.current_streak,
                longestStreak: lastResult.longest_streak,
                streakUpdated: lastResult.streak_updated,
            });

            if (claimedRef.current.size > maxTrackedIds) claimedRef.current.clear();
        }, delayMs);

        return () => {
            cancelled = true;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [challenges, enabled, isLoading, delayMs, claimReward, showXPGain, maxTrackedIds]);

    return {
        /** Still claimable after auto-claiming — the fallback rows in the streak modal. */
        pendingCount: challenges.filter((c) => c.is_completed && !c.is_claimed).length,
    };
};
